import { useAuth } from '@clerk/clerk-react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useState, useEffect, useRef } from 'react';
import { Link } from 'wouter';
import {
  api,
  type VendorQuoteIntakeListResponse,
  type VendorQuoteIntakeListItem,
  type VendorQuoteIntakeStatus,
} from '@/lib/api';

interface Props {
  selectedId: string | null;
}

// Status → display config. Order matters: actionable statuses first.
export const INTAKE_STATUS_META: Record<
  VendorQuoteIntakeStatus,
  { label: string; className: string }
> = {
  ambiguous:           { label: 'Needs pick',     className: 'bg-blue-100 text-blue-800' },
  unmatched:           { label: 'Unmatched',      className: 'bg-amber-100 text-amber-800' },
  parse_failed:        { label: 'Parse failed',   className: 'bg-red-100 text-red-800' },
  auto_committed:      { label: 'Auto-committed',  className: 'bg-emerald-100 text-emerald-800' },
  manually_committed:  { label: 'Committed',      className: 'bg-gray-100 text-gray-700' },
  discarded:           { label: 'Discarded',      className: 'bg-gray-100 text-gray-500' },
};

const FILTERS: Array<{ key: VendorQuoteIntakeStatus | 'all'; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'ambiguous', label: 'Needs pick' },
  { key: 'unmatched', label: 'Unmatched' },
  { key: 'parse_failed', label: 'Parse failed' },
  { key: 'auto_committed', label: 'Auto-committed' },
  { key: 'manually_committed', label: 'Committed' },
  { key: 'discarded', label: 'Discarded' },
];

export function VendorInboxList({ selectedId }: Props) {
  const { getToken } = useAuth();
  const [filter, setFilter] = useState<VendorQuoteIntakeStatus | 'all'>('all');

  const {
    data, isLoading, isError, error,
    fetchNextPage, hasNextPage, isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['vendor-quote-intake', { filter }],
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams();
      if (filter !== 'all') params.set('status', filter);
      if (pageParam) params.set('cursor', pageParam as string);
      const qs = params.toString();
      return api<VendorQuoteIntakeListResponse>(
        `/api/vendor-quote-intake${qs ? '?' + qs : ''}`,
        { tokenGetter: () => getToken() },
      );
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: 5_000,
  });

  const items = data?.pages.flatMap((p) => p.items) ?? [];

  // Infinite scroll: observe a sentinel near the bottom of the list.
  const scrollRef = useRef<HTMLUListElement | null>(null);
  const sentinelRef = useRef<HTMLLIElement | null>(null);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasNextPage) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) fetchNextPage();
      },
      { root: scrollRef.current, rootMargin: '200px' },
    );
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, items.length]);

  return (
    <div className="h-full flex flex-col">
      <div className="p-2 border-b border-border bg-background flex-shrink-0 flex flex-wrap gap-1">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-2 py-1 text-xs rounded font-medium transition-colors ${
              filter === f.key
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-foreground-muted hover:bg-muted/70'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isLoading && <div className="p-4 text-sm text-muted-foreground">Loading…</div>}
      {isError && (
        <div className="p-4 text-sm text-destructive">
          {(error as { message?: string })?.message ?? 'Failed to load inbox'}
        </div>
      )}

      <ul ref={scrollRef} className="flex-1 overflow-auto">
        {items.map((it) => (
          <IntakeRow key={it.id} it={it} selected={it.id === selectedId} />
        ))}
        {!isLoading && items.length === 0 && (
          <li className="p-4 text-sm italic text-muted-foreground">
            {filter === 'all'
              ? 'No vendor quote emails yet. They appear here as quotes@ receives them.'
              : 'No intakes in this status.'}
          </li>
        )}
        <li ref={sentinelRef} />
        {isFetchingNextPage && (
          <li className="p-3 text-xs text-center text-foreground-muted">Loading more…</li>
        )}
        {!hasNextPage && items.length > 0 && (
          <li className="p-3 text-xs text-center text-foreground-muted">
            {items.length} intake{items.length === 1 ? '' : 's'} · end of list
          </li>
        )}
      </ul>
    </div>
  );
}

function IntakeRow({ it, selected }: { it: VendorQuoteIntakeListItem; selected: boolean }) {
  const meta = INTAKE_STATUS_META[it.status];
  const when = it.receivedAt ?? it.createdAt;
  return (
    <li>
      <Link
        href={`/vendor-inbox/${it.id}`}
        className={`block px-4 py-3 border-b border-border hover:bg-muted/40 ${
          selected ? 'bg-muted/60' : ''
        }`}
      >
        <div className="flex items-baseline justify-between gap-2 flex-wrap">
          <div className="font-semibold text-sm truncate">
            {it.vendorName ?? it.senderName ?? it.senderEmail ?? 'Unknown sender'}
          </div>
          <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${meta.className}`}>
            {meta.label}
          </span>
        </div>
        <div className="text-xs text-foreground-muted truncate mt-0.5">
          {it.subject ?? '(no subject)'}
        </div>
        <div className="text-xs text-foreground-muted mt-1 flex items-center gap-2 flex-wrap">
          {it.quoteCount > 0 && (
            <span>
              {it.quoteCount} quote{it.quoteCount === 1 ? '' : 's'}
            </span>
          )}
          {it.partNumbers.length > 0 && (
            <span className="font-mono truncate">{it.partNumbers.slice(0, 3).join(', ')}{it.partNumbers.length > 3 ? '…' : ''}</span>
          )}
          <span className="ml-auto">{new Date(when).toLocaleDateString()}</span>
        </div>
      </Link>
    </li>
  );
}
